import { type FC, Fragment } from 'react';

import { Box, ListSubheader, Typography } from '@packages/shared';

import type { SxProps } from '@mui/system';
import { ensureUniqueness } from '../../utils/ensureUniqueness';
import type { GroupedFilterValues, SimpleFilterValue } from '../../api/rest/types';
import { useSelect } from '../../hooks/useSelect';
import { SimpleFilterValueGrid } from '../SimpleFilterValueGrid';

export type SimpleFilterValueGridListProps = {
  /** Multiple groups of filter values */
  groupedValues: GroupedFilterValues<SimpleFilterValue>[];
  /** Currently selected filter value ids */
  selectedIds: string[];
  /** Callback fired when the filter selection changes */
  onChange?: (selectedIds: string[]) => void;
  /** Additional styles which should be applied on the container */
  contentContainerStyle?: SxProps;
};

/**
 * A list of multiple grids of simple filter value tiles, each with a header
 * */
export const SimpleFilterValueGridList: FC<SimpleFilterValueGridListProps> = ({
  groupedValues,
  selectedIds,
  onChange,
  contentContainerStyle,
}) => {
  const uniqueGroups = ensureUniqueness(groupedValues);

  const { toggleId } = useSelect([selectedIds, onChange]);

  return (
    <Box
      component="ul"
      sx={{
        margin: 0,
        padding: 2,
        listStyleType: 'none',
        overflowY: 'auto',
        ...contentContainerStyle,
      }}
    >
      {uniqueGroups.map(({ groupName, values }) => (
        <Fragment key={groupName}>
          {groupName && (
            <Typography
              component={ListSubheader}
              sx={{ fontWeight: 500, marginTop: 0, marginBottom: 1, paddingLeft: 0 }}
            >
              {groupName}
            </Typography>
          )}
          <Box component="li" sx={{ '&:not(:first-of-type)': { marginTop: 2 } }}>
            <SimpleFilterValueGrid values={values} selectedIds={selectedIds} onClick={toggleId} />
          </Box>
        </Fragment>
      ))}
    </Box>
  );
};
